"use client";

import { logError } from "@jetblue/dotcom-fe-logger";

const CONSENT_CHANGE_MARKER_KEY = "dotcom-consent-change-marker";

type ConsentMarkerRead =
  | {
      marker: null | string;
      succeeded: true;
    }
  | {
      succeeded: false;
    };
const state = {
  documentConsentChangeMarker: null as null | string,
  isConsentSynchronizationRegistered: false,
  isReloadRequested: false,
  isStorageErrorReported: false,
};

const reportStorageError = (error: unknown) => {
  if (state.isStorageErrorReported) {
    return;
  }

  state.isStorageErrorReported = true;

  void logError({
    error,
    message: "Consent synchronization storage is unavailable",
  });
};

const readConsentChangeMarker = (): ConsentMarkerRead => {
  try {
    return {
      marker: localStorage.getItem(CONSENT_CHANGE_MARKER_KEY),
      succeeded: true,
    };
  } catch (error) {
    reportStorageError(error);
    return { succeeded: false };
  }
};

const reloadCurrentDocument = () => {
  if (state.isReloadRequested) {
    return;
  }

  state.isReloadRequested = true;
  globalThis.location.reload();
};

const reloadIfConsentMarkerChanged = () => {
  const markerRead = readConsentChangeMarker();

  if (
    !markerRead.succeeded ||
    markerRead.marker !== state.documentConsentChangeMarker
  ) {
    reloadCurrentDocument();
  }
};

const handleConsentMarkerStorage = (event: StorageEvent) => {
  if (event.key === CONSENT_CHANGE_MARKER_KEY) {
    reloadIfConsentMarkerChanged();
  }
};

const handleConsentPageShow = (event: PageTransitionEvent) => {
  if (event.persisted) {
    reloadIfConsentMarkerChanged();
  }
};

export const registerConsentSynchronization = () => {
  if (
    state.isConsentSynchronizationRegistered ||
    typeof globalThis.addEventListener !== "function"
  ) {
    return;
  }

  state.isConsentSynchronizationRegistered = true;

  const markerRead = readConsentChangeMarker();
  state.documentConsentChangeMarker = markerRead.succeeded
    ? markerRead.marker
    : null;

  globalThis.addEventListener("storage", handleConsentMarkerStorage);
  globalThis.addEventListener("pageshow", handleConsentPageShow);
};
export const invalidateConsentDocuments = () => {
  try {
    const nextMarker = new Date().toLocaleString();

    state.documentConsentChangeMarker = nextMarker;
    localStorage.setItem(CONSENT_CHANGE_MARKER_KEY, nextMarker);
  } catch (error) {
    reportStorageError(error);
  }

  reloadCurrentDocument();
};
